import { AfterViewInit, Component, ElementRef, inject, input, model, viewChild, output } from '@angular/core';
import {
  AgCartesianAxisOptions,
  AgChart,
  AgChartOptions,
  AgColumnSeriesOptions,
  AgLineSeriesOptions,
} from 'ag-charts-community';
import { AgChartInstance, AgSeriesNodeClickParams } from 'ag-charts-community/dist/esm/es6/chart/agChartOptions';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent } from '../../base/base.component';
import { Colours } from '../../../constants';
import { BarChartAxisType } from '../../../models';
import { FormatService } from '../../../services';

@Component({
  selector: 'app-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrl: './bar-chart.component.scss',
})
export class BarChartComponent<T> extends BaseComponent implements AfterViewInit {
  private readonly formatService = inject(FormatService);

  private readonly chart = viewChild<ElementRef<HTMLDivElement>>('chart');

  public readonly header = input('');
  public readonly subHeader = input('');
  public readonly xKey = input<string>();
  public readonly xAxisName = input<string>();
  public readonly xFormat = input<BarChartAxisType>(BarChartAxisType.text);
  public readonly xRotation = input(0);

  public readonly yKey = input<string>();
  public readonly yName = input<string>();
  public readonly yAxisName = input<string>();
  public readonly yFormat = input<BarChartAxisType>(BarChartAxisType.text);
  public readonly yRotation = input(0);

  public readonly yKeyRight = input<string>();
  public readonly yNameRight = input<string>();
  public readonly yAxisNameRight = input<string>();
  public readonly yFormatRight = input<BarChartAxisType>(BarChartAxisType.text);
  public readonly yRotationRight = input(0);

  public readonly showLegend = input(false);
  public readonly data$ = model<Observable<T[]>>();
  public readonly subHeaderFunc = input<(data: T[]) => string>();
  public readonly dateFormatFunc = input<(date: Date) => string>();
  public readonly clicked = output<T>();

  private agChart: AgChartInstance;
  private options: AgChartOptions;

  ngAfterViewInit(): void {
    this.drawChart();
  }

  formatSummary(count: number, total: number): string {
    return (
      `${$localize`Total`}: ${this.formatValue(total, this.yFormat(), false)}, ` +
      `${$localize`Count`}: ${this.formatValue(count, this.yFormatRight(), true)}`
    );
  }

  private drawChart() {
    this.data$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: T[]) => {
        this.getOptions(data);
        if (!this.agChart) {
          this.agChart = AgChart.create(this.options);
        } else {
          AgChart.update(this.agChart, this.options);
        }
      });
  }

  private getOptions(data: T[]) {
    this.options = {
      autoSize: true,
      container: this.chart()?.nativeElement,
      title: {
        text: this.header(),
      },
      subtitle: {
        text: this.subHeaderFunc() ? this.subHeaderFunc()(data) : this.subHeader(),
      },
      data: data,
      series: this.getSeries(),
      axes: this.getAxes(data),
      legend: {
        enabled: this.showLegend(),
        position: 'bottom',
      },
    };
  }

  private getSeries(): (AgColumnSeriesOptions | AgLineSeriesOptions)[] {
    const series: (AgColumnSeriesOptions | AgLineSeriesOptions)[] = [
      {
        type: 'column',
        xKey: this.xKey(),
        yKey: this.yKey(),
        yName: this.yName() ?? this.yKey(),
        fill: Colours.blue,
        ...this.seriesDefaults(),
      },
    ];

    if (this.yKeyRight()) {
      series.push({
        type: 'line',
        xKey: this.xKey(),
        yKey: this.yKeyRight(),
        yName: this.yNameRight() ?? this.yKeyRight(),
        ...this.seriesDefaults(),
      });
    }

    return series;
  }

  private seriesDefaults(): AgLineSeriesOptions {
    return {
      highlightStyle: {
        item: {
          fill: Colours.green,
        },
      },
      tooltip: {
        renderer: (params) => {
          return {
            title: `${this.xAxisName() ? `${this.xAxisName()}: ` : ''}${this.formatValue(params.xValue, this.xFormat())}`,
            content: this.formatSummary(params.datum[this.yKeyRight()], params.datum[this.yKey()]),
          };
        },
      },
      listeners: {
        nodeClick: (event: AgSeriesNodeClickParams<any>) => this.clicked.emit(event.datum),
      },
    };
  }

  private getAxes(data: T[]): AgCartesianAxisOptions[] {
    const axes: AgCartesianAxisOptions[] = [
      {
        title: {
          text: this.xAxisName(),
        },
        position: 'bottom',
        ...this.axisDefaults(this.xFormat(), this.xRotation()),
      },
      {
        title: {
          text: this.yAxisName(),
        },
        position: 'left',
        keys: [this.yKey()],
        ...this.axisDefaults(this.yFormat(), this.yRotation()),
      },
    ];

    if (this.yKeyRight()) {
      axes.push({
        title: {
          text: this.yAxisNameRight(),
        },
        position: 'right',
        keys: [this.yKeyRight()],
        ...this.axisDefaults(this.yFormatRight(), this.yRotationRight()),
      });
    }

    return axes;
  }

  private axisDefaults(type: BarChartAxisType, rotation: number): any {
    return {
      type: type === BarChartAxisType.number || type === BarChartAxisType.currency ? 'number' : 'category',
      tick: {
        minSpacing: 5,
      },
      label: {
        rotation: rotation,
        formatter: (params) => this.formatValue(params.value, type),
      },
    };
  }

  private formatValue(value: any, type: BarChartAxisType, rounded = true): any {
    switch (type) {
      case BarChartAxisType.number:
        return rounded ? this.formatService.numberShort(value) : this.formatService.number(value);
      case BarChartAxisType.currency:
        return rounded ? this.formatService.currencyShort(value, '£') : this.formatService.currency(value, '£');
      case BarChartAxisType.date:
        return this.dateFormatFunc()
          ? this.dateFormatFunc()(value)
          : `${this.formatService.dayOfWeekShort(value)} ${this.formatService.date(value)}`;
      default:
        return value;
    }
  }
}
